import { eq, find, float32Eq, isNumber, objectHasKeys } from '@art-suite/art-core-ts';

import { getResolvedContractorId } from './contractorLib';
import { ContractorIdsByTrade, getEffectiveScanValue, ScanValue } from './ScanRoot';
import {
  ScanGroupOptimized,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanPartOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
} from './ScanRootOptimized';
import { getEffectiveTaskTrade, getTotalCost, isGroupScoped, isScanPartScoped, isTaskScoped } from './StandardScanTemplateRoot/StandardScanGetters';

// Historical note: 'price' refers to the material market price (incoming value)
// while 'cost' fields (laborCost, materialCost, totalCost) are price × quantity
const SCOPE_VALUES = ['quantity', 'price', 'laborCost', 'materialCost', 'totalCost', 'laborPrice'];
// KAIDEV-4181: `tenantResponsibility` is intentionally NOT here. It is a purely
// informational classification tag (it moves no dollars: getTotalCost is
// materialCost + laborCost only), so toggling it must never create a change order.
const SCOPING_TAGS = ['deferred'];

// ***************************************************************************************************
// Lib Functions
// ***************************************************************************************************

/**
 * Normalize boolean tag values so that false and undefined are treated as equivalent.
 * This is important for boolean flags where both false and undefined mean "not enabled".
 * For non-boolean values, returns the value as-is.
 */
const normalizeTagValue = (value: string | number | boolean | undefined): string | number | boolean | undefined => {
  if (value === false) return undefined;
  return value;
};

// Suppliers aren't always known by a formal id (a manually-entered supplier
// name may have no productSupplierId at all), so either field changing counts
// as the supplier changing.
const productSupplierChanged = (scanPart: ScanPartOptimized, baselineScanPart: ScanPartOptimized) =>
  scanPart.skuDetails?.productSupplierId !== baselineScanPart.skuDetails?.productSupplierId
  || scanPart.skuDetails?.productSupplier !== baselineScanPart.skuDetails?.productSupplier;

const scopingTagsChanged = (scanPart: ScanPartOptimized, baselineScanPart: ScanPartOptimized) => {
  return SCOPING_TAGS.some(tag => normalizeTagValue(scanPart.tags?.[tag]) !== normalizeTagValue(baselineScanPart.tags?.[tag]));
};

const contractorAssignmentChanged = (
  scanPart: ScanPartOptimized,
  baselineScanPart: ScanPartOptimized,
  defaultContractorsByTrade?: ContractorIdsByTrade,
  baselineDefaultContractorsByTrade?: ContractorIdsByTrade,
) => {
  const trade = scanPart.scanPartType === 'task'
    ? getEffectiveTaskTrade(scanPart as ScanTaskOptimized)
    : scanPart.template.trade;
  const baselineTrade = baselineScanPart.scanPartType === 'task'
    ? getEffectiveTaskTrade(baselineScanPart as ScanTaskOptimized)
    : baselineScanPart.template.trade;
  const effectiveAssignedContractorId = getResolvedContractorId(trade, scanPart.assignedContractorId, defaultContractorsByTrade);
  const effectiveBaselineAssignedContractorId = getResolvedContractorId(
    baselineTrade,
    baselineScanPart.assignedContractorId,
    baselineDefaultContractorsByTrade,
  );
  return effectiveAssignedContractorId !== effectiveBaselineAssignedContractorId;
};

const scopeValueChanged = (value1: ScanValue | undefined, value2: ScanValue | undefined) => {
  if (value1 === value2) return false;
  if (!value1 || !value2) return value1 !== value2;
  // compare effective values: an override masks changes to the underlying calculation, and pricing follows the effective value
  const effectiveValue1 = getEffectiveScanValue(value1);
  const effectiveValue2 = getEffectiveScanValue(value2);
  return isNumber(effectiveValue1) && isNumber(effectiveValue2)
    ? !float32Eq(effectiveValue1, effectiveValue2)
    : effectiveValue1 !== effectiveValue2;
};

const scopeValuesChanged = (scanPart: ScanPartOptimized, baselineScanPart: ScanPartOptimized) =>
  SCOPE_VALUES.find(value => scopeValueChanged(scanPart.values?.[value], baselineScanPart.values?.[value]));

const childrenHaveScopeChanges = <T extends ScanPartOptimized>(
  childrenById: Record<string, T> | undefined,
  baselineChildrenById: Record<string, T> | undefined,
  customScanPartHasScopeChanges?: (child: T, baselineChild: T) => boolean,
): boolean => {
  const haveChildren = objectHasKeys(childrenById);
  const haveBaselineChildren = objectHasKeys(baselineChildrenById);
  if (!haveChildren && !haveBaselineChildren) return false;
  if (!haveChildren) return !!find(baselineChildrenById, isScanPartScoped); // any scoped baseline children deleted?
  if (!haveBaselineChildren) return !!find(childrenById, isScanPartScoped); // any scoped children added?

  for (const id in childrenById) {
    const child = childrenById[id];
    const baselineChild = baselineChildrenById?.[id];
    if (scanPartHasScopeChanges(child, baselineChild, customScanPartHasScopeChanges)) return true;
  }
  // check for deleted, scoped children
  for (const id in baselineChildrenById) {
    if (!isScanPartScoped(baselineChildrenById[id])) continue;
    if (!childrenById?.[id]) return true;
  }
  return false;
};

/**
 * Return true if any of the change-order-relevant common to all ScanParts have changed
 *
 * Common ScanPart scope-changes checked:
 * - scanPart deleted or added
 * - scoped status (isScanPartScoped changed)
 * - sku changed
 * - product supplier/vendor changed
 * - scoping tags changed (e.g. deferred; NOT tenantResponsibility — see SCOPING_TAGS / KAIDEV-4181)
 * - scope values changed (e.g. quantity, price, laborCost, materialCost, totalCost, laborPrice)
 */
const scanPartHasScopeChanges = <T extends ScanPartOptimized>(
  scanPart: T | undefined | null,
  baselineScanPart: T | undefined | null,
  customScanPartHasScopeChanges?: (scanPart: T, baselineScanPart: T) => boolean,
): boolean => {
  if (scanPart === baselineScanPart) return false; // identical; no changes

  const scanPartIsScoped = isScanPartScoped(scanPart);
  const baselineScanPartIsScoped = isScanPartScoped(baselineScanPart);
  if (scanPartIsScoped !== baselineScanPartIsScoped) return true; // scoped status changed

  if (!scanPartIsScoped) return false; // scan-part was unscoped before and is unscoped now, all other changes are irrelevant.

  // Note: from here, we know scanPartIsScoped AND baselineScanPartIsScoped are true
  // Note: Therefor the next two lines should never be triggered, but we're leaving them for defensive coding
  if (!baselineScanPart) return scanPartIsScoped; // scoped scan-part was added
  if (!scanPart) return baselineScanPartIsScoped; // scoped scan-part was deleted

  if (getTotalCost(scanPart) !== getTotalCost(baselineScanPart)) return true; // cost changed

  if (scanPart.skuId !== baselineScanPart?.skuId) return true; // sku changed
  if (productSupplierChanged(scanPart, baselineScanPart)) return true; // supplier/vendor changed
  if (scopingTagsChanged(scanPart, baselineScanPart)) return true; // scoping tags changed
  if (scopeValuesChanged(scanPart, baselineScanPart)) return true; // scope values changed

  if (customScanPartHasScopeChanges) return customScanPartHasScopeChanges(scanPart, baselineScanPart);
  return false;
};

const optionHasScopeChanges = (option: ScanOptionOptimized | undefined, baselineOption: ScanOptionOptimized | undefined): boolean =>
  scanPartHasScopeChanges(option, baselineOption, (option, baselineOption) => childrenHaveScopeChanges(option.lineItemsById, baselineOption.lineItemsById));

// ***************************************************************************************************
// Exported Functions
// ***************************************************************************************************

/**
 * Return true if any of the standard scanPart change-order-relevant scalar-props have changed
 * OR if the task is scoped and the contractor assignment has changed
 * OR any of the task's options have scope changes
 */
export const taskHasScopeChanges = (
  task: ScanTaskOptimized | null | undefined,
  baselineTask: ScanTaskOptimized | null | undefined,
  defaultContractorsByTrade: ContractorIdsByTrade | undefined,
  baselineDefaultContractorsByTrade: ContractorIdsByTrade | undefined,
): boolean => {
  // Check contractor changes even when task refs are identical (defaults may differ)
  if (
    task && baselineTask && isScanPartScoped(task)
    && contractorAssignmentChanged(task, baselineTask, defaultContractorsByTrade, baselineDefaultContractorsByTrade)
  ) {
    return true;
  }

  return scanPartHasScopeChanges(
    task,
    baselineTask,
    (task, baselineTask) => childrenHaveScopeChanges(task.optionsById, baselineTask.optionsById, optionHasScopeChanges),
  );
};

/**
 * Return true if any of the standard scanPart change-order-relevant scalar-props have changed
 * OR if any of the group's tasks have scope changes
 */
export const groupHasScopeChanges = (
  group: ScanGroupOptimized | null | undefined,
  baselineGroup: ScanGroupOptimized | null | undefined,
  defaultContractorsByTrade: ContractorIdsByTrade | undefined,
  baselineDefaultContractorsByTrade: ContractorIdsByTrade | undefined,
) =>
  scanPartHasScopeChanges(group, baselineGroup, (group, baselineGroup) =>
    childrenHaveScopeChanges(
      group.tasksById,
      baselineGroup.tasksById,
      (task, baselineTask) => taskHasScopeChanges(task, baselineTask, defaultContractorsByTrade, baselineDefaultContractorsByTrade),
    ));

/**
 * What a change order did to a line item: put it in the scope, took it out, or changed it. A changed line item may be
 * one that replaced the baseline's, so the two can have different ids.
 */
export type LineItemChangeOrder =
  | { kind: 'added'; lineItem: ScanLineItemOptimized }
  | { kind: 'removed'; baselineLineItem: ScanLineItemOptimized }
  | { kind: 'changed'; lineItem: ScanLineItemOptimized; baselineLineItem: ScanLineItemOptimized };

/**
 * What a change order did to a line item, by the rules that decide whether there's a change order at all
 * (ABOUT_CHANGE_ORDERS.md): added or removed when its scoped status moved, else changed. Undefined when the change
 * order leaves the line item alone.
 */
const getLineItemChangeOrder = (
  lineItem: ScanLineItemOptimized | undefined,
  baselineLineItem: ScanLineItemOptimized | undefined,
): LineItemChangeOrder | undefined => {
  if (!scanPartHasScopeChanges(lineItem, baselineLineItem)) return undefined;
  if (!isScanPartScoped(baselineLineItem)) return { kind: 'added', lineItem: lineItem! };
  if (!isScanPartScoped(lineItem)) return { kind: 'removed', baselineLineItem: baselineLineItem! };
  return { kind: 'changed', lineItem: lineItem!, baselineLineItem: baselineLineItem! };
};

/**
 * A task's line items of one line item template in its options of one option template, in row order. Each is a row of
 * the template, whichever of those options holds it, so a deleted and re-added option keeps its rows.
 */
const getTemplateLineItems = (task: ScanTaskOptimized | undefined, optionTemplateId: string, lineItemTemplateId: string) =>
  (task?.options ?? [])
    .filter(option => option.templateId === optionTemplateId)
    .flatMap(option => option.lineItems ?? [])
    .filter(lineItem => lineItem.templateId === lineItemTemplateId);

/**
 * What a change order did to the line items behind one row of a line item template in a task: the row of
 * `lineItemId`, or the template's preview row when that's undefined. A scoped line item deleted from the template and
 * one put in reads as one change when they're its only two. Any other deleted one is listed on the template's first
 * row, so no removal goes unshown.
 * @param task - The task, as the change order has it
 * @param baselineTask - The task as approved; undefined when the change order added it
 */
export const getLineItemRowChangeOrders = (
  task: ScanTaskOptimized | undefined,
  baselineTask: ScanTaskOptimized | undefined,
  optionTemplateId: string,
  lineItemTemplateId: string,
  lineItemId: string | undefined,
): LineItemChangeOrder[] => {
  const lineItems = getTemplateLineItems(task, optionTemplateId, lineItemTemplateId);
  const baselineLineItems = getTemplateLineItems(baselineTask, optionTemplateId, lineItemTemplateId);
  const baselineLineItemsById = Object.fromEntries(baselineLineItems.map(lineItem => [lineItem.id, lineItem]));
  const deletedLineItems = baselineLineItems.filter(baselineLineItem =>
    isScanPartScoped(baselineLineItem) && !lineItems.some(lineItem => lineItem.id === baselineLineItem.id)
  );
  const newLineItems = lineItems.filter(lineItem => isScanPartScoped(lineItem) && !baselineLineItemsById[lineItem.id]);
  const replacement = deletedLineItems.length === 1 && newLineItems.length === 1
    ? { lineItem: newLineItems[0], baselineLineItem: deletedLineItems[0] }
    : undefined;

  const lineItem = lineItems.find(lineItem => lineItem.id === lineItemId);
  const lineItemChangeOrder: LineItemChangeOrder | undefined = lineItem && lineItem === replacement?.lineItem
    ? { kind: 'changed', ...replacement }
    : getLineItemChangeOrder(lineItem, lineItem && baselineLineItemsById[lineItem.id]);
  const rowListsRemovals = !replacement && lineItemId === lineItems[0]?.id;
  return [
    ...(lineItemChangeOrder ? [lineItemChangeOrder] : []),
    ...(rowListsRemovals ? deletedLineItems.map(baselineLineItem => ({ kind: 'removed' as const, baselineLineItem })) : []),
  ];
};

/**
 * Check if any scoped task would have its effective contractor changed due to different defaults.
 * This handles the case where defaultContractorsByTrade changes but task objects are identical (structural sharing).
 * It's possible that groups or tasks may be === same, but the effective contractors change because of a change in the defaults.
 */
const anyScopedTaskAffectedByDefaultContractorChange = (
  scanRoot: ScanRootOptimized,
  defaultContractorsByTrade: ContractorIdsByTrade | undefined,
  baselineDefaultContractorsByTrade: ContractorIdsByTrade | undefined,
): boolean => {
  if (eq(defaultContractorsByTrade, baselineDefaultContractorsByTrade)) return false; // no change in defaults

  for (const group of scanRoot.groups ?? []) {
    if (!isGroupScoped(group)) continue;
    for (const task of group.tasks ?? []) {
      if (isTaskScoped(task) && !task.assignedContractorId) {
        // Intentionally passing task as both scanPart and baselineScanPart -- we only need to check if the effective contractor changed due to the default-changes
        if (contractorAssignmentChanged(task, task, defaultContractorsByTrade, baselineDefaultContractorsByTrade)) {
          return true;
        }
      }
    }
  }
  return false;
};

export const scanRootHasScopeChanges = (scanRoot: ScanRootOptimized, baselineScanRoot: ScanRootOptimized) => {
  if (scanRoot === baselineScanRoot) return false;
  if (!baselineScanRoot) return true;

  if (
    scanPartHasScopeChanges(scanRoot, baselineScanRoot, (scanRoot, baselineScanRoot) =>
      childrenHaveScopeChanges(
        scanRoot.groupsById,
        baselineScanRoot.groupsById,
        (group, baselineGroup) => groupHasScopeChanges(group, baselineGroup, scanRoot.defaultContractorsByTrade, baselineScanRoot.defaultContractorsByTrade),
      ))
  ) return true;

  // Check if defaultContractorsByTrade change affects any scoped task
  if (
    anyScopedTaskAffectedByDefaultContractorChange(
      scanRoot,
      scanRoot.defaultContractorsByTrade,
      baselineScanRoot.defaultContractorsByTrade,
    )
  ) return true;

  return false;
};

export const contractorAssignmentChangedDueToChangedDefaults = (
  task: ScanTaskOptimized,
  baselineTask: ScanTaskOptimized,
  defaultContractorsByTrade?: ContractorIdsByTrade,
  baselineDefaultContractorsByTrade?: ContractorIdsByTrade,
) => {
  const effectiveAssignedContractorId = getResolvedContractorId(getEffectiveTaskTrade(task), task.assignedContractorId, defaultContractorsByTrade);
  const effectiveBaselineAssignedContractorId = getResolvedContractorId(
    getEffectiveTaskTrade(baselineTask),
    baselineTask.assignedContractorId,
    baselineDefaultContractorsByTrade,
  );
  return !task.assignedContractorId && effectiveAssignedContractorId && (effectiveBaselineAssignedContractorId !== effectiveAssignedContractorId);
};
